import collectionQuickview from "./collection-quickview";
import productItemApp from "./product-item-app";

/**
 * Collection Pagination
 */
function collectionPagination() {
  const c = {
    dom: {
      collection: "collection",
      collectionProducts: "productsGrid",
      collectionPagination: ".js-collection-pagination",
      collectionItem: ".js-pi",
      next: ".next a"
    },
    cls: {
      hide: "hide"
    },
    isIntersecting: false,
    observer: null
  };

  // Globals
  const $collection = document.getElementById(c.dom.collection);
  const $collectionProducts = document.getElementById(c.dom.collectionProducts);
  const $collectionPagination = $collection?.querySelector(c.dom.collectionPagination);
  let $next = $collectionPagination?.querySelector(c.dom.next) || null;

  let isLoading = false;
  let fetchRequestPending = false;
  let atEndIntervalId = null;
  const SCROLL_DEBOUNCE_MS = 300;
  const AT_END_POLL_MS = 100;

  /**
   * User is "at end" when the bottom of the viewport is near the bottom of the
   * collection. Uses distance so it stays true after new products are appended
   * (page grows, user stays at bottom visually).
   */
  function isAtEndOfCollection() {
    const viewportBottom = window.scrollY + window.innerHeight;
    const collectionBottom = $collection.offsetTop + $collection.offsetHeight;
    const distanceToBottom = collectionBottom - viewportBottom;
    const nearBottomThreshold = window.innerHeight * 1.5;
    return distanceToBottom < nearBottomThreshold && distanceToBottom > -200;
  }

  function clearAtEndInterval() {
    if (atEndIntervalId) {
      clearInterval(atEndIntervalId);
      atEndIntervalId = null;
    }
  }

  function startOrStopAtEndInterval() {
    if (!isAtEndOfCollection() || !$next) {
      clearAtEndInterval();
      return;
    }
    if (atEndIntervalId) { return; }
    atEndIntervalId = setInterval(() => {
      if (!isAtEndOfCollection() || !$next) {
        clearAtEndInterval();
        return;
      }
      fetchWhenScrolledOverHalfCollection();
    }, AT_END_POLL_MS);
  }

  /**
   * Init
   */
  function init() {
    if (!$collection || !$next) {return;}

    const debouncedScrollCheck = __.debounce(() => {
      fetchWhenScrolledOverHalfCollection();
      startOrStopAtEndInterval();
    }, SCROLL_DEBOUNCE_MS);

    __.addEvent({
      id: document,
      event: "scroll",
      fn: () => debouncedScrollCheck()
    });

    // Auto-fetch when filters hide most products (no scroll needed)
    window.addEventListener("requestMoreProducts", () => {
      if (!$next) { return; }
      if (isLoading) {
        fetchRequestPending = true;
        return;
      }
      isLoading = true;
      fetchMoreData();
    });
  }

  function fetchWhenScrolledOverHalfCollection() {
    const currentScroll = window.scrollY;
    const collectionHeight = $collection.offsetHeight;
    const seventyFivePercentHeight = (collectionHeight * 0.75);
    const isScrolledOverSeventyFivePercent = currentScroll > seventyFivePercentHeight;
    const shouldFetch = isScrolledOverSeventyFivePercent || isAtEndOfCollection();

    if (currentScroll > collectionHeight) { return; }
    if (!shouldFetch || !$next || isLoading) { return; }

    isLoading = true;
    fetchMoreData();
  }

  function maybeAutoFetchAfterFilter() {
    if (!$next) { return; }
    if (!isAtEndOfCollection()) { return; }

    isLoading = true;
    fetchMoreData();
  }

  /**
   * Fetch more data
   */
  function fetchMoreData() {
    // Get the base URL from the pagination link
    const baseUrl = $next.getAttribute("href");

    // Get current URL parameters (including filters)
    const currentParams = new URLSearchParams(window.location.search);
    let nextPageUrl;

    if (baseUrl.includes("?")) {
      const baseUrlObj = new URL(baseUrl, window.location.origin);
      const baseParams = new URLSearchParams(baseUrlObj.search);

      currentParams.forEach((value, key) => {
        if (key !== "page") {
          baseParams.set(key, value);
        }
      });

      nextPageUrl = `${baseUrlObj.pathname}?${baseParams.toString()}&view=ajax`;
    } else {
      nextPageUrl = `${baseUrl}?${currentParams.toString()}&view=ajax`;
    }

    __.ajax({
      error: onError,
      success: onSuccess,
      type: "GET",
      url: nextPageUrl
    });
  }

  /**
   * Handle error
   */
  function onError(error) {
    console.warn("onError", error);
    isLoading = false;
  }

  /**
   * Handle success
   */
  function onSuccess(data) {
    if (data) {
      const $nextCollection = new DOMParser().parseFromString(data, "text/html");
      const $nextCollectionProducts = $nextCollection.getElementById(c.dom.collectionProducts);
      const $nextCollectionPagination = $nextCollection.querySelector(c.dom.collectionPagination);
      const $newNext = $nextCollectionPagination?.querySelector(c.dom.next);

      if ($collectionProducts
          && $nextCollectionProducts
          && $nextCollectionProducts.querySelectorAll(c.dom.collectionItem).length > 0
      ) {
        $collectionProducts.append(...$nextCollectionProducts.childNodes);

        productItemApp({
          dom: {
            parent: "collection"
          }
        });
        // set quick view and video events after new products and touts are added
        window.dispatchEvent(new CustomEvent("productsLoaded"));
        collectionQuickview();
        SDG.videoHTML();
      }

      if ($newNext) {
        $next = $newNext;
      } else {
        $next = false;
      }

      // Short settle, then process any queued fetch request
      setTimeout(() => {
        isLoading = false;
        if (fetchRequestPending) {
          fetchRequestPending = false;
          if ($next) {
            isLoading = true;
            fetchMoreData();
          }
        } else {
          maybeAutoFetchAfterFilter();
        }
      }, 50);
    }
  }

  return init();
}

export default collectionPagination;
